import React from 'react';
import { Redirect, Route, RouteProps } from 'react-router-dom';
import { Result, Spin } from 'antd';
import { PermissionName } from './permissionsConstants';
import { usePermission } from '../../hooks/usePermission';
import { checkPermission } from '../../utils/permissionUtils';
import './PermissionRoute.scss';


export type NoPermissionMode = 
  | 'redirect'
  | '403'
  | 'overlay'
  | 'blur';

interface PermissionRouteProps extends RouteProps {
  /**
   * needed permission
   */
  permission?: PermissionName | PermissionName[];

  /**
   * needed any permission
   */
  anyPermission?: PermissionName[];

  /**
   * needed all permissions
   */
  allPermissions?: PermissionName[];

  /**
   * redirect path when no permission (used when mode='redirect')
   */
  redirectTo?: string;

  /**
   * no permission mode (default 'redirect')
   */
  mode?: NoPermissionMode;

  /**
   * custom no permission message
   */
  noPermissionMessage?: string;
}

export const NoPermission403: React.FC<{
  message?: string;
  redirectTo?: string;
}> = ({ message, redirectTo }) => {
  return (
    <div className="permission-route-403">
      <Result
        status="403"
        title="403"
        subTitle={message || "Sorry, you don't have permission to access this page."}
        // extra={
        //   redirectTo ? (
        //     <Button type="primary" href={redirectTo}>
        //       Back Home
        //     </Button>
        //   ) : (
        //     <Button type="primary" onClick={() => window.history.back()}>
        //       Go Back
        //     </Button>
        //   )
        // }
      />
    </div>
  );
};

const NoPermissionOverlay: React.FC<{ 
  message?: string;
  redirectTo?: string;
  children: React.ReactNode;
}> = ({ message, redirectTo, children }) => {
  return (
    <div className="permission-route-overlay-wrapper">
      {children}
      <div className="permission-route-overlay">
        <div className="permission-route-overlay-content">
          <Result
            status="403"
            title="403"
            subTitle={message || "You don't have permission to view this content."}
            // extra={
            //   redirectTo ? (
            //     <Button type="primary" href={redirectTo}>
            //       Go to Home
            //     </Button>
            //   ) : (
            //     <Button type="primary" onClick={() => window.history.back()}>
            //       Go Back
            //     </Button>
            //   )
            // }
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Blur effect component for no permission
 */
const NoPermissionBlur: React.FC<{ 
  message?: string;
  redirectTo?: string;
  children: React.ReactNode;
}> = ({ message, redirectTo, children }) => {
  return (
    <div className="permission-route-blur-wrapper">
      <div className="permission-route-blur-content">
        {children}
      </div>
      <div className="permission-route-blur-overlay">
        <div className="permission-route-blur-message">
          <Result
            status="403"
            //title="403"
            subTitle={message || "You don't have permission to view this content."}
            // extra={
            //   redirectTo ? (
            //     <Button type="primary" href={redirectTo}>
            //       Go to Home
            //     </Button>
            //   ) : (
            //     <Button type="primary" onClick={() => window.history.back()}>
            //       Go Back
            //     </Button>
            //   )
            // }
          />
        </div>
      </div>
    </div>
  );
};

export const PermissionRoute: React.FC<PermissionRouteProps> = ({
  permission,
  anyPermission,
  allPermissions,
  redirectTo,
  mode = '403',
  noPermissionMessage,
  component: Component,
  render,
  children,
  ...rest
}) => {
  const {
    userPermissions,
    hasPermission,
    hasAnyPermission: checkAnyPermission,
    hasAllPermissions: checkAllPermissions,
    isLoading
  } = usePermission();

  return (
    <Route
      {...rest}
      render={(props) => {
        // If permissions are loading and no cached permissions exist, show loading state instead of 403
        if (isLoading && !userPermissions) {
          return (
            <div className="d-flex align-items-center flex-column vh-100 justify-content-center text-center py-3">
              <div className="d-flex align-items-center flex-column px-4">
                <Spin size="large" />
              </div>
              <div className="text-muted font-size-xl text-center pt-3">
                Loading...
              </div>
            </div>
          );
        }

        // Check permissions in render function, only executed when route matches
        // Use unified permission check function
        const hasAccess = checkPermission({
          permission,
          anyPermission,
          allPermissions,
          hasPermission,
          hasAnyPermission: checkAnyPermission,
          hasAllPermissions: checkAllPermissions
        });

        const renderContent = () => {
          if (Component) {
            return <Component {...props} />;
          }
          if (render) {
            return render(props);
          }
          if (typeof children === 'function') {
            return children(props);
          }
          return <>{children}</>;
        };

        if (hasAccess) {
          return renderContent();
        }

        switch (mode) {
          case 'redirect':
            if (redirectTo) {
              return <Redirect to={redirectTo} />;
            }
            return <Redirect to="/" />;

          case '403':
            return (
              <NoPermission403 
                message={noPermissionMessage}
                redirectTo={redirectTo}
              />
            );

          case 'overlay':
            return (
              <NoPermissionOverlay
                message={noPermissionMessage}
                redirectTo={redirectTo}
              >
                {renderContent()}
              </NoPermissionOverlay>
            );

          case 'blur':
            return (
              <NoPermissionBlur
                message={noPermissionMessage}
                redirectTo={redirectTo}
              >
                {renderContent()}
              </NoPermissionBlur>
            );

          default:
            return (
              <NoPermission403 
                message={noPermissionMessage}
              />
            );
        }
      }}
    />
  );
};

export default PermissionRoute;

